import type { Event as SentryEvent } from "@sentry/react"

const NETWORK_FAILURE_MESSAGES = [
	/^Failed to fetch( \(.+\))?$/,
	/^NetworkError when attempting to fetch resource\.( \(.+\))?$/,
	/^Load failed( \(.+\))?$/,
	/^The network connection was lost\.( \(.+\))?$/,
	/^The Internet connection appears to be offline\.( \(.+\))?$/,
]

// Each browser's wording for a request that never reached the server (offline,
// dropped Wi-Fi, a laptop waking up); no code change can fix those.
export function isBrowserNetworkFailure(event: SentryEvent): boolean {
	const values = event.exception?.values ?? []
	if (values.length === 0) return false
	return values.every(
		(value) =>
			value.type === "TypeError" &&
			NETWORK_FAILURE_MESSAGES.some((pattern) =>
				pattern.test(value.value ?? ""),
			),
	)
}

/** A thrown fetch error that means the request never got a response. */
export function isFetchNetworkError(error: unknown): boolean {
	return (
		error instanceof TypeError &&
		NETWORK_FAILURE_MESSAGES.some((pattern) => pattern.test(error.message))
	)
}

const BROWSER_STORAGE_FAILURES = [
	/Encountered full disk while opening backing store for indexedDB\.open\.?$/,
	/Internal error opening backing store for indexedDB\.open\.?$/,
	/^Expected IndexedDB not found: /,
	/The transaction was aborted, so the request cannot be fulfilled\.?$/,
	/Failed to execute 'objectStore' on 'IDBTransaction': The transaction has finished\.?$/,
	/Attempt to get a record from database without an in-progress transaction\.?$/,
]

export function isBrowserStorageFailure(event: SentryEvent): boolean {
	const values = event.exception?.values ?? []
	if (values.length === 0) return false
	return values.every((value) =>
		BROWSER_STORAGE_FAILURES.some((pattern) => pattern.test(value.value ?? "")),
	)
}

// A save rejected while Zero was disconnected; the outage itself is reported once
// by the Zero connection reporter, so each failed save would only repeat it.
export function isZeroConnectionLoss(event: SentryEvent): boolean {
	return (event.exception?.values ?? []).some(
		(value) => value.type === "ZeroConnectionError",
	)
}

const LINK_SCANNER_REJECTION =
	/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/

// Outlook's Safe Links scanner opens emailed links in a headless browser that
// rejects with this; no visitor ever sees it.
export function isLinkScannerRejection(event: SentryEvent): boolean {
	return (event.exception?.values ?? []).some((value) =>
		LINK_SCANNER_REJECTION.test(value.value ?? ""),
	)
}

// A request cancelled on purpose (navigating away, a component unmounting)
// rejects with this; nothing failed for the user.
export function isAbortedRequest(event: SentryEvent): boolean {
	const values = event.exception?.values ?? []
	return (
		values.length > 0 &&
		values.every(
			(value) =>
				value.type === "AbortError" ||
				(value.value ?? "").startsWith("AbortError: "),
		)
	)
}
